import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam, type ExamState } from '../../exam.js';
import { Submission } from '../../submission.js';
import { resolveTaskTreePath, taskTreeIndexPathString } from '../../utils/task-tree-path.js';

export const setTaskScoreToolDef = [
  {
    type: 'function',
    function: {
      name: 'set_task_score',
      description:
        'Setzt die Punktzahl einer Aufgabe für EINE Schülerabgabe MANUELL auf einen exakten Wert (Lehrer-Override) — OHNE erneute KI-Bewertung. ' +
        'Nutze dies NUR, wenn es um die Bewertung einer konkreten Abgabe geht (typischerweise bei offenem Korrektur-/Antworten-Canvas oder wenn ein Schüler genannt wird). ' +
        'NICHT verwenden, wenn die MAXIMALPUNKTZAHL im Bewertungsschema gemeint ist (typischerweise bei offenem MS/GS-Editor, oder wenn "Bewertungsschema"/"Erwartungshorizont"/"maximal" genannt wird) — dafür stattdessen refine_grading_scheme nutzen, dort NIE nach einem Schülernamen fragen. ' +
        'Für eine (Neu-)Bewertung durch die KI ohne vorgegebene Punktzahl nutze run_grading_agent. ' +
        'Wenn taskNumber und/oder submissionId/studentName fehlen, wird automatisch die aktuell im Correction-/Antworten-Canvas sichtbare Aufgabe bzw. der sichtbare Schüler verwendet, sofern eindeutig — frage den Lehrer nur nach, wenn auch dort nichts Eindeutiges sichtbar ist.',
      parameters: {
        type: 'object',
        properties: {
          score: { type: 'number', description: 'Die zu vergebende Punktzahl für die inhaltliche Bewertung.' },
          taskNumber: {
            type: 'string',
            description:
              'Aufgabennummer, z.B. "3" oder "2a". Optional, wenn im Canvas-Kontext eine eindeutige Aufgabe sichtbar ist.',
          },
          submissionId: {
            type: 'string',
            description:
              'Interne Submission-ID, z.B. aus dem UI-Kontext (sichtbarer Schüler) oder vorherigen Tool-Ergebnissen. Optional.',
          },
          studentName: {
            type: 'string',
            description: 'Name des Schülers, falls submissionId nicht bekannt ist. Optional.',
          },
          explanation: {
            type: 'string',
            description: 'Optionale kurze Begründung für die Punktzahl.',
          },
        },
        required: ['score'],
      },
    },
  },
];

interface NestedSubmissionTask {
  _id?: string;
  taskNumber?: string;
  subTasksSubmission?: NestedSubmissionTask[];
  gradingResult?: Record<string, unknown>;
}

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export async function handleSetTaskScore(exam: Exam, toolCallArgs: any): Promise<object> {
  const examIdToUse = exam._id?.toString();
  if (!examIdToUse) return { content: 'Fehler: Keine Klausur-ID.' };

  const score = Number(toolCallArgs?.score);
  if (!Number.isFinite(score)) {
    return { content: 'Fehler: Ungültige Punktzahl.' };
  }

  // Purely informational canvas context (see prompts.ts KONTEXT ZUR AKTUELLEN UI) — used here only
  // as a fallback default, never overriding an explicit taskNumber/submissionId/studentName.
  const canvasContext = (exam as any).canvasContext ?? {};
  const canvasHasVisibleTask = canvasContext?.view === 'correction' || canvasContext?.view === 'answers';

  const requestedTaskNumber = String(toolCallArgs?.taskNumber ?? '').trim();
  const taskNumber =
    requestedTaskNumber || (canvasHasVisibleTask ? String(canvasContext?.selectedTaskNumber ?? '').trim() : '');
  if (!taskNumber) {
    return {
      content:
        'SYSTEM INFO: Aufgabennummer fehlt und im Canvas ist aktuell keine eindeutige Aufgabe sichtbar. Bitte den Lehrer nach der Aufgabennummer fragen.',
    };
  }

  const submissionCollection = await getCollection(Submission);

  let submissionId = String(toolCallArgs?.submissionId ?? '').trim();
  const studentName = String(toolCallArgs?.studentName ?? '').trim();

  if (!submissionId && studentName) {
    const match = await submissionCollection.findOne({
      examId: examIdToUse,
      studentName: { $regex: `^${escapeRegExp(studentName)}$`, $options: 'i' },
    } as any);
    if (!match) {
      return { content: `SYSTEM INFO: Keine Abgabe von "${studentName}" für diese Klausur gefunden.` };
    }
    submissionId = String((match as any)._id);
  }

  if (!submissionId && canvasHasVisibleTask && canvasContext?.selectedStudentId) {
    submissionId = String(canvasContext.selectedStudentId);
  }

  if (!submissionId) {
    return {
      content:
        'SYSTEM INFO: Kein Schüler angegeben und im Canvas ist aktuell keine eindeutige Abgabe sichtbar. Bitte den Lehrer nach dem Schüler fragen.',
    };
  }

  const submission = (await submissionCollection.findOne({ _id: submissionId } as any)) as any;
  if (!submission || String(submission.examId) !== examIdToUse) {
    return { content: 'SYSTEM INFO: Abgabe nicht gefunden.' };
  }

  const resolved = resolveTaskTreePath(
    submission.taskSubmissions as NestedSubmissionTask[] | undefined,
    taskNumber,
    'subTasksSubmission',
  );
  if (resolved.status === 'not_found') {
    return { content: `SYSTEM INFO: Aufgabe "${taskNumber}" wurde in dieser Abgabe nicht gefunden.` };
  }
  if (resolved.status === 'ambiguous') {
    const candidatePaths = resolved.taskNumberPaths.map(p => p.join('.'));
    return {
      content: `SYSTEM INFO: Aufgabennummer "${taskNumber}" ist mehrdeutig, sie kommt an mehreren Stellen vor (${candidatePaths.join(', ')}). Bitte die vollständige Aufgabennummer angeben (z.B. "${candidatePaths[0]}").`,
    };
  }
  const path = taskTreeIndexPathString('taskSubmissions', 'subTasksSubmission', resolved.indexPath);

  const explanation = typeof toolCallArgs?.explanation === 'string' ? toolCallArgs.explanation.trim() : '';
  const newGrading = {
    ...(resolved.node.gradingResult || {}),
    score,
    actualPointsGiven: score,
    isApproved: true,
    ...(explanation ? { explanation } : {}),
  };

  await submissionCollection.updateOne({ _id: submissionId } as any, {
    $set: { [`${path}.gradingResult`]: newGrading },
  });

  const studentLabel = submission.studentName ? ` (${submission.studentName})` : '';
  return {
    content: `SYSTEM INFO: Punktzahl für Aufgabe ${taskNumber}${studentLabel} auf ${score} gesetzt und als geprüft markiert.`,
  };
}

export function setTaskScoreAvailability(examState: ExamState): boolean {
  return Boolean(examState?.hasGradingStarted);
}
